"use client";

import React from "react";
import { motion } from "framer-motion";
import { BadgeCheck, FileText, ShoppingBag, Recycle, MessageCircle, SunMedium } from "lucide-react";

const features = [
  {
    num: "01",
    icon: BadgeCheck,
    title: "Verified Suppliers Network",
    desc: "Every manufacturer, distributor, and recycler on our platform is screened before listing. You deal with real, verified businesses only.",
  },
  {
    num: "02",
    icon: FileText,
    title: "Smart Quotation System",
    desc: "Request quotes from multiple suppliers with a single form. Compare proposals side by side and make decisions backed by real data.",
  },
  {
    num: "03",
    icon: ShoppingBag,
    title: "Complete Solar Marketplace",
    desc: "Browse solar panels, batteries, inverters, charge controllers, and full solar kits. Filter by category, provider, and rating.",
  },
  {
    num: "04",
    icon: Recycle,
    title: "Eco-Friendly Recycling",
    desc: "Connect with certified recyclers to dispose of aging solar hardware responsibly. A marketplace for the full product lifecycle.",
  },
  {
    num: "05",
    icon: MessageCircle,
    title: "Personal Consultations",
    desc: "Not sure what you need? Book a free consultation. A real person will call you back and walk you through the best options.",
  },
  {
    num: "06",
    icon: SunMedium,
    title: "Solar Energy Calculator",
    desc: "Estimate your energy needs before you buy. Our calculator helps homeowners and businesses size their solar systems correctly.",
  },
];

export default function FeatureSection() {
  return (
    <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-medium text-[#17BC2E] uppercase tracking-widest mb-3"
          >
            Built for the entire solar ecosystem
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-[#080A09] text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
          >
            Powerful Features for Everyone
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map(({ num, icon: Icon, title, desc }, i) => (
            <motion.div
              key={num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              whileHover={{ y: -4 }}
              className="group bg-[#002301] rounded-2xl p-7 flex flex-col gap-4 cursor-default transition-all duration-300 hover:shadow-xl hover:shadow-[#002301]/20"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-[#17BC2E]/30 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-[#17BC2E]" />
                </div>
                <span className="text-[#FFB600] text-lg font-bold">{num}</span>
              </div>
              <div>
                <h3 className="text-white font-semibold text-lg mb-2 leading-snug">{title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
